/* Maison Fleur – virtual try-on (pilot).
   Everything happens in the browser: the shopper's photo is never uploaded.
   MediaPipe Pose finds the shoulders; the bag is scaled to the shoulder width
   and its handles are placed on the shoulder. The shopper can drag, resize and flip. */
(function () {
  'use strict';
  var root = document.querySelector('[data-tryon]');
  if (!root) return;

  function q(s) { return root.querySelector(s); }
  function qa(s) { return Array.prototype.slice.call(root.querySelectorAll(s)); }

  var STR = {};
  try { STR = JSON.parse(q('[data-tryon-strings]').textContent); } catch (e) {}

  var MP = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
  var MODEL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
  var MAX_SIDE = 1600;
  var BAG_TO_SHOULDER = 1.45; // drawn bag width relative to shoulder width (a 45 cm tote vs ~32 cm shoulders)

  var canvas = q('[data-tryon-canvas]');
  var ctx = canvas.getContext('2d');
  var drop = q('[data-tryon-drop]');
  var statusEl = q('[data-tryon-status]');
  var tools = q('[data-tryon-tools]');
  var dragHint = q('[data-tryon-drag-hint]');
  var saveBtn = q('[data-tryon-save]');
  var addBtn = q('[data-tryon-add]');
  var nameEl = q('[data-tryon-name]');
  var variantInput = q('[data-tryon-variant]');
  var bagButtons = qa('[data-tryon-bag]');

  var photo = null;          // offscreen canvas with the (resized) photo
  var W = 0, H = 0;
  var pose = null;           // { sw, right:{x,y}, left:{x,y}, ang } in photo px
  var bag = null;            // current bag button
  var bagImg = null;
  var cache = {};
  var place = { x: 0, y: 0, w: 0, rot: 0, side: 1 };
  var scaleMul = 1;
  var landmarkerP = null;

  /* ---------- pose model (lazy) ---------- */
  // Shopify minifies theme JS and rewrites dynamic import(), so the ES module is loaded through an injected module script.
  function importVision() {
    if (window.__mfVision) return Promise.resolve(window.__mfVision);
    return new Promise(function (resolve, reject) {
      var cb = '__mfVisionReady' + Date.now();
      var timer = setTimeout(function () { delete window[cb]; reject(new Error('vision timeout')); }, 25000);
      window[cb] = function (v) { clearTimeout(timer); delete window[cb]; window.__mfVision = v; resolve(v); };
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = 'import * as v from "' + MP + '/vision_bundle.mjs"; window["' + cb + '"](v);';
      document.head.appendChild(s);
    });
  }
  function loadLandmarker() {
    if (landmarkerP) return landmarkerP;
    landmarkerP = importVision().then(function (v) {
      return v.FilesetResolver.forVisionTasks(MP + '/wasm').then(function (files) {
        function opts(delegate) {
          return { baseOptions: { modelAssetPath: MODEL, delegate: delegate }, runningMode: 'IMAGE', numPoses: 1 };
        }
        return v.PoseLandmarker.createFromOptions(files, opts('GPU')).catch(function () {
          return v.PoseLandmarker.createFromOptions(files, opts('CPU'));
        });
      });
    });
    landmarkerP.catch(function () { landmarkerP = null; });
    return landmarkerP;
  }
  // warm the model up as soon as the shopper shows intent
  drop.addEventListener('pointerdown', function () { loadLandmarker().catch(function () {}); }, { once: true });

  /* ---------- images ---------- */
  function loadImage(src) {
    if (cache[src]) return cache[src];
    cache[src] = new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
    return cache[src];
  }

  function readPhoto(file) {
    function viaImg() {
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = reject;
        img.src = url;
      });
    }
    var p = window.createImageBitmap ? createImageBitmap(file, { imageOrientation: 'from-image' }).catch(viaImg) : viaImg();
    return p.then(function (src) {
      var sw = src.width, sh = src.height;
      var k = Math.min(1, MAX_SIDE / Math.max(sw, sh));
      var c = document.createElement('canvas');
      c.width = Math.round(sw * k); c.height = Math.round(sh * k);
      c.getContext('2d').drawImage(src, 0, 0, c.width, c.height);
      return c;
    });
  }

  /* ---------- placement ---------- */
  function readPose(lm) {
    if (!lm) return null;
    var L = lm[11], R = lm[12];
    if (!L || !R) return null;
    if ((L.visibility || 1) < 0.3 && (R.visibility || 1) < 0.3) return null;
    var a = { x: L.x * W, y: L.y * H }, b = { x: R.x * W, y: R.y * H };
    var sw = Math.hypot(a.x - b.x, a.y - b.y);
    var hl = lm[23], hr = lm[24];
    if (hl && hr && (hl.visibility || 1) > 0.3 && (hr.visibility || 1) > 0.3) {
      // turned bodies make the shoulders look narrow: the torso length keeps the scale honest
      var torso = Math.hypot((a.x + b.x) / 2 - (hl.x + hr.x) / 2 * W, (a.y + b.y) / 2 - (hl.y + hr.y) / 2 * H);
      sw = Math.max(sw, torso * 0.62);
    }
    if (sw < Math.min(W, H) * 0.06) return null;
    var right = a.x > b.x ? a : b, left = a.x > b.x ? b : a;
    return { sw: sw, right: right, left: left, ang: Math.atan2(right.y - left.y, right.x - left.x) };
  }

  function autoPlace() {
    if (pose) {
      var s = place.side > 0 ? pose.right : pose.left;
      place.w = pose.sw * BAG_TO_SHOULDER * scaleMul;
      place.x = s.x;
      place.y = s.y - pose.sw * 0.08;
      place.rot = Math.max(-0.2, Math.min(0.2, pose.ang * 0.5));
    } else {
      place.w = Math.min(W, H) * 0.42 * scaleMul;
      place.x = place.side > 0 ? W * 0.66 : W * 0.34;
      place.y = H * 0.3;
      place.rot = 0;
    }
  }

  /* ---------- drawing ---------- */
  function render(forExport) {
    if (!photo) return;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
    ctx.save();
    ctx.filter = 'brightness(1.03) saturate(1.06) contrast(1.02)';
    ctx.drawImage(photo, 0, 0);
    ctx.restore();
    // a touch of warm afternoon light, the same mood as the site
    ctx.save();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.fillStyle = 'rgba(255, 206, 150, 0.14)';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();

    if (bagImg) {
      var w = place.w, h = w * bagImg.height / bagImg.width;
      var ax = w * (parseFloat(bag.getAttribute('data-anchor')) || 50) / 100;
      ctx.save();
      ctx.translate(place.x, place.y);
      ctx.rotate(place.rot);
      if (place.side < 0) ctx.scale(-1, 1);
      ctx.shadowColor = 'rgba(45, 32, 20, 0.32)';
      ctx.shadowBlur = w * 0.07;
      ctx.shadowOffsetX = w * 0.03;
      ctx.shadowOffsetY = w * 0.05;
      ctx.drawImage(bagImg, -ax, 0, w, h);
      ctx.restore();
    }

    if (forExport) {
      var fs = Math.round(Math.max(14, W * 0.026));
      ctx.save();
      ctx.font = '500 ' + fs + 'px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(251, 248, 242, 0.92)';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
      ctx.shadowBlur = fs * 0.5;
      if ('letterSpacing' in ctx) ctx.letterSpacing = Math.round(fs * 0.25) + 'px';
      ctx.fillText('MAISON FLEUR', W / 2, H - fs * 1.4);
      ctx.restore();
    }
  }

  /* ---------- ui ---------- */
  function setStatus(text, ms) {
    statusEl.textContent = text || '';
    statusEl.hidden = !text;
    if (text && ms) setTimeout(function () { if (statusEl.textContent === text) statusEl.hidden = true; }, ms);
  }

  function selectBag(btn) {
    bag = btn;
    bagButtons.forEach(function (b) { b.setAttribute('aria-checked', b === btn ? 'true' : 'false'); });
    var available = btn.getAttribute('data-available') === 'true';
    variantInput.value = btn.getAttribute('data-variant') || '';
    nameEl.textContent = btn.getAttribute('data-name') || '';
    addBtn.disabled = !available || !variantInput.value;
    return loadImage(btn.getAttribute('data-src')).then(function (img) {
      if (bag !== btn) return;
      bagImg = img;
      render(false);
    });
  }

  bagButtons.forEach(function (b) {
    b.addEventListener('click', function () { selectBag(b); });
  });

  function usePhoto(file) {
    if (!file || !/^image\//.test(file.type)) return;
    setStatus(STR.loading);
    var model = loadLandmarker().catch(function () { return null; });
    readPhoto(file).then(function (c) {
      photo = c; W = c.width; H = c.height; pose = null; scaleMul = 1; place.side = 1;
      drop.hidden = true;
      canvas.hidden = false;
      tools.hidden = false;
      saveBtn.hidden = false;
      dragHint.hidden = false;
      root.classList.add('has-photo');
      autoPlace();
      render(false);
      return model.then(function (lmk) {
        if (!lmk) { setStatus(STR.noPose, 5000); return; }
        var res = lmk.detect(photo);
        pose = readPose(res && res.landmarks && res.landmarks[0]);
        autoPlace();
        render(false);
        setStatus(pose ? '' : STR.noPose, pose ? 0 : 5000);
      });
    }).catch(function () { setStatus(STR.error, 5000); });
  }

  qa('[data-tryon-file]').forEach(function (input) {
    input.addEventListener('change', function () {
      usePhoto(input.files && input.files[0]);
      input.value = '';
    });
  });

  // drag a photo onto the drop zone (desktop)
  ['dragover', 'drop'].forEach(function (t) {
    drop.addEventListener(t, function (e) {
      e.preventDefault();
      if (t === 'drop' && e.dataTransfer && e.dataTransfer.files[0]) usePhoto(e.dataTransfer.files[0]);
    });
  });

  qa('[data-tryon-size]').forEach(function (b) {
    b.addEventListener('click', function () {
      var f = b.getAttribute('data-tryon-size') === '1' ? 1.08 : 1 / 1.08;
      scaleMul = Math.max(0.55, Math.min(1.9, scaleMul * f));
      place.w = place.w * f;
      render(false);
    });
  });

  q('[data-tryon-flip]').addEventListener('click', function () {
    place.side = -place.side;
    if (pose) autoPlace(); else place.x = W - place.x;
    render(false);
  });

  q('[data-tryon-reset]').addEventListener('click', function () {
    scaleMul = 1;
    autoPlace();
    render(false);
  });

  /* drag with one finger, resize with two */
  var pointers = {};
  var start = null;
  function toPhoto(e) {
    var r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) * W / r.width, y: (e.clientY - r.top) * H / r.height };
  }
  canvas.addEventListener('pointerdown', function (e) {
    canvas.setPointerCapture(e.pointerId);
    pointers[e.pointerId] = toPhoto(e);
    var ids = Object.keys(pointers);
    if (ids.length === 1) start = { p: pointers[e.pointerId], x: place.x, y: place.y };
    if (ids.length === 2) {
      var a = pointers[ids[0]], b = pointers[ids[1]];
      start = { d: Math.hypot(a.x - b.x, a.y - b.y), w: place.w, mul: scaleMul };
    }
    dragHint.hidden = true;
  });
  canvas.addEventListener('pointermove', function (e) {
    if (!pointers[e.pointerId] || !start) return;
    pointers[e.pointerId] = toPhoto(e);
    var ids = Object.keys(pointers);
    if (ids.length === 1 && start.p) {
      var p = pointers[ids[0]];
      place.x = start.x + (p.x - start.p.x);
      place.y = start.y + (p.y - start.p.y);
    } else if (ids.length === 2 && start.d) {
      var a = pointers[ids[0]], b = pointers[ids[1]];
      var k = Math.hypot(a.x - b.x, a.y - b.y) / start.d;
      scaleMul = Math.max(0.55, Math.min(1.9, start.mul * k));
      place.w = start.w * (scaleMul / start.mul);
    }
    render(false);
  });
  function endPointer(e) {
    delete pointers[e.pointerId];
    var ids = Object.keys(pointers);
    start = ids.length === 1 ? { p: pointers[ids[0]], x: place.x, y: place.y } : null;
  }
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);

  saveBtn.addEventListener('click', function () {
    render(true);
    canvas.toBlob(function (blob) {
      render(false);
      if (!blob) return;
      var name = 'maison-fleur-' + (bag ? (bag.getAttribute('data-name') || 'look').toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'look') + '.jpg';
      var file = window.File ? new File([blob], name, { type: 'image/jpeg' }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({ files: [file], title: STR.shareTitle || 'Maison Fleur' }).catch(function () {});
        return;
      }
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    }, 'image/jpeg', 0.9);
  });

  /* initial bag: ?bag=handle or the first one */
  var wanted = (new URLSearchParams(window.location.search).get('bag') || '').toLowerCase();
  var first = bagButtons.filter(function (b) { return wanted && (b.getAttribute('data-url') || '').toLowerCase().indexOf('/' + wanted) > -1; })[0] ||
    bagButtons.filter(function (b) { return b.hasAttribute('data-initial'); })[0] || bagButtons[0];
  if (first) selectBag(first);

  // test hook: window.MFTryOn.load(url) feeds an image URL as if it was uploaded
  window.MFTryOn = {
    load: function (url) {
      return fetch(url).then(function (r) { return r.blob(); }).then(function (b) {
        usePhoto(new File([b], 'test.jpg', { type: b.type || 'image/jpeg' }));
      });
    },
    state: function () { return { pose: pose, place: place, W: W, H: H }; }
  };
})();
